import { FC, Fragment, useEffect, useRef, useState } from 'react'
import { FormDataConsumer, required, SelectInput, ReferenceInput, useChoicesContext } from 'react-admin'
import { CertificatesCreatePKI } from './ZakenPki.form'
import { CertificatesCreateZaakClient } from './ZakenClient.form'
import { useFormContext, useWatch } from 'react-hook-form'
import { InstanceOut } from '../../../../types/apiRequests'

export const INSTANCES_PER_PAGE = 100

// Lifts the instances already loaded by the ReferenceInput, so no second request is needed.
const InstanceChoicesListener: FC<{ onChange: (instances: InstanceOut[]) => void }> = ({ onChange }) => {
  const { allChoices } = useChoicesContext<InstanceOut>()
  // Keyed on content: a new array identity per render must not loop through setState.
  const key = JSON.stringify(allChoices ?? [])
  useEffect(() => {
    onChange(JSON.parse(key) as InstanceOut[])
  }, [key, onChange])
  return null
}

const HostSelectorFromInstance: FC<{ instances: InstanceOut[] }> = ({ instances }) => {
  const { setValue } = useFormContext()
  const instanceId = useWatch({ name: 'metaData.instanceId' }) as string | undefined
  const previousInstanceId = useRef(instanceId)
  const selected = instances.find((i) => i.id === instanceId)
  const hosts = selected?.hosts ?? []

  // Host, label and interface belong to the previous instance: drop them on a switch.
  useEffect(() => {
    if (previousInstanceId.current === instanceId) return
    previousInstanceId.current = instanceId
    setValue('metaData.commonName', '')
    setValue('metaData.instanceName', '')
    setValue('metaData.interface', null)
  }, [instanceId, setValue])

  // Defaults of the selected instance: its label and, when it has only one, its host.
  useEffect(() => {
    if (!selected) return
    if (selected.hosts.length === 1) {
      setValue('metaData.commonName', selected.hosts[0], { shouldValidate: true, shouldDirty: true })
    }
    if (selected.label) {
      setValue('metaData.instanceName', selected.label, { shouldValidate: true, shouldDirty: true })
    }
  }, [selected, setValue])

  if (!instanceId) return null

  // Always mounted once an instance is chosen, so a missing host blocks submit.
  // readOnly, not disabled: disabled inputs skip validation and are not submitted.
  return (
    <SelectInput
      source="metaData.commonName"
      label="resources.certificates.fields.host"
      choices={hosts.map((h) => ({ id: h, name: h }))}
      readOnly={hosts.length === 0}
      fullWidth
      validate={[required()]}
    />
  )
}

export const ZakenForm: FC = () => {
  const [instances, setInstances] = useState<InstanceOut[]>([])

  return (
    <Fragment>
      <ReferenceInput
        source="metaData.instanceId"
        reference="instances"
        perPage={INSTANCES_PER_PAGE}
        isRequired
      >
        <>
          <SelectInput
            label="resources.certificates.fields.instance"
            optionValue="id"
            optionText={(choice: InstanceOut) =>
              choice?.label ? `${choice.label} (${choice.fqdn})` : choice?.fqdn
            }
            emptyText={undefined}
            fullWidth
            validate={[required()]}
          />
          <InstanceChoicesListener onChange={setInstances} />
        </>
      </ReferenceInput>

      <HostSelectorFromInstance instances={instances} />

      <FormDataConsumer>
        {({ formData }) => {
          if (formData?.type === 'pki') {
            return <CertificatesCreatePKI />
          }
          if (formData?.type === 'zaak_client') {
            return <CertificatesCreateZaakClient />
          }
          return null
        }}
      </FormDataConsumer>
    </Fragment>
  )
}